"use client";

import { useState, useEffect, useRef } from "react";
import { useInView } from "motion/react";

interface DecodeTextProps {
  text: string;
  className?: string;
  delay?: number;
  /** When true, animation starts immediately without waiting for element to be in view */
  immediate?: boolean;
  /** Custom margin for inView trigger (e.g. "0px 0px -50% 0px" to trigger when 50% up screen) */
  inViewMargin?: string;
}

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// Track which character indices have been resolved for opacity
type CharState = {
  char: string;
  resolved: boolean;
};

export function DecodeText({
  text,
  className = "",
  delay = 0,
  immediate = false,
  inViewMargin = "0px 0px -20% 0px",
}: DecodeTextProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin:
      inViewMargin as `${number}${"px" | "%"} ${number}${"px" | "%"} ${number}${"px" | "%"} ${number}${"px" | "%"}`,
  });
  const [charStates, setCharStates] = useState<CharState[]>(
    text.split("").map((char) => ({ char, resolved: false }))
  );

  const shouldAnimate = immediate || isInView;

  useEffect(() => {
    if (!shouldAnimate) return;

    // Start with random characters (all unresolved)
    setCharStates(
      text.split("").map((char) => ({
        char:
          char === " "
            ? " "
            : (CHARS[Math.floor(Math.random() * CHARS.length)] ?? "A"),
        resolved: false,
      }))
    );

    const startTimer = setTimeout(() => {
      const targetText = text;
      const iterations = 12;
      let currentIteration = 0;

      const interval = setInterval(() => {
        currentIteration++;

        setCharStates(
          text.split("").map((char, index) => {
            if (char === " ") return { char: " ", resolved: true };

            const progress = currentIteration / iterations;
            const shouldResolve = index / targetText.length < progress;

            if (shouldResolve) {
              return { char: targetText[index] ?? "", resolved: true };
            }

            return {
              char: CHARS[Math.floor(Math.random() * CHARS.length)] ?? "A",
              resolved: false,
            };
          })
        );

        if (currentIteration >= iterations) {
          clearInterval(interval);
          setCharStates(
            text.split("").map((char) => ({ char, resolved: true }))
          );
        }
      }, 120); // Slowed down from 80ms to 120ms (50% slower)

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(startTimer);
  }, [shouldAnimate, text, delay]);

  return (
    <span ref={ref} className={className}>
      {charStates.map((state, index) => (
        <span key={index} style={{ opacity: state.resolved ? 1 : 0.5 }}>
          {state.char}
        </span>
      ))}
    </span>
  );
}
